import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { api, fileUrl, fmtDate, rupiah } from "@/lib/api";
import { txt } from "@/lib/site";
import { useLenis } from "@/components/motion";
import { Navbar, Hero } from "@/components/landing/Hero";
import { Marquee, About, NigInfo, Schedule } from "@/components/landing/Story";
import { EventsStrip, DevotionBlock, Komsel, Gallery, PastoralForm, Footer } from "@/components/landing/Community";
import { Sermons, ServeUpInvite } from "@/components/landing/Sermon";
import { OurPastor, OurTeam } from "@/components/landing/Team";

function SpecialEventsBanner({ items = [], site }) {
  if (!items.length) return null;
  return (
    <section className="bg-slate-950 py-16 md:py-24 overflow-hidden" data-testid="special-events-section">
      <div className="max-w-6xl mx-auto px-6">
        <p className="text-[11px] font-black uppercase tracking-[0.3em] text-amber-400 flex items-center gap-2"><Sparkles className="w-4 h-4" /> {txt(site, "special_label", "Event Khusus")}</p>
        <div className="mt-8 grid gap-5 md:grid-cols-2">
          {items.slice(0, 2).map((e, i) => (
            <motion.div key={e.id} initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="group relative overflow-hidden rounded-[2rem] min-h-64 flex flex-col justify-end p-7 border border-white/10" data-testid={`special-event-${e.id}`}>
              {e.image_url && <img src={fileUrl(e.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-60 group-hover:scale-105 transition duration-700" />}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
              <div className="relative">
                <span className={`text-[10px] font-black uppercase tracking-widest rounded-full px-3 py-1 ${(e.price || 0) > 0 ? "bg-amber-400 text-slate-900" : "bg-emerald-400 text-slate-900"}`}>{(e.price || 0) > 0 ? rupiah(e.price) : "Gratis"}</span>
                <h3 className="mt-3 text-2xl md:text-3xl font-black text-white tracking-tight">{e.title}</h3>
                <p className="mt-1 text-sm text-white/60">{e.banner_text || `${fmtDate(e.start_date)} · ${e.location}`}</p>
                <Link to="/register" data-testid={`special-event-cta-${e.id}`} className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-amber-300 hover:gap-3 transition-all">Daftar via Portal <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WorshipPosts({ items = [], site }) {
  if (!items.length) return null;
  return (
    <section className="bg-white border-y border-slate-100 py-20 md:py-28" data-testid="worship-posts-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <p className="text-[11px] font-black uppercase tracking-[0.3em] text-blue-600">{txt(site, "worship_label", "Ibadah & Pengumuman")}</p>
        <p className="mt-3 text-sm text-slate-500">Tanpa registrasi — datang dan bawa keluargamu.</p>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((e, i) => (
            <motion.article key={e.id} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-[1.75rem] overflow-hidden bg-slate-50 border border-slate-100 hover:-translate-y-1 transition duration-300" data-testid={`worship-post-${e.id}`}>
              {e.image_url && <img src={fileUrl(e.image_url)} alt={e.title} className="w-full h-56 object-cover" />}
              <div className="p-6">
                <p className="text-lg font-extrabold text-slate-900">{e.title}</p>
                <p className="mt-1 text-sm text-slate-500">{fmtDate(e.start_date)}{e.time ? ` · ${e.time} WITA` : ""}{e.location ? ` · ${e.location}` : ""}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  useLenis();
  const [data, setData] = useState({ schedules: [], events: [], komsels: [], gallery: [] });
  const [site, setSite] = useState(null);
  useEffect(() => {
    const load = () => {
      api.get("/public/home").then((r) => setData(r.data)).catch(() => {});
      api.get("/public/site").then((r) => setSite(r.data)).catch(() => {});
    };
    load();
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  return (
    <div className="bg-slate-50 text-slate-800" data-testid="landing-page">
      <Navbar />
      <Hero schedules={data.schedules} heroImages={site?.hero_images || []} badge={site?.hero_badge} site={site} />
      <ServeUpInvite />
      <SpecialEventsBanner items={data.special_events || []} site={site} />
      <WorshipPosts items={data.worship_posts || []} site={site} />
      <OurPastor site={site} />
      <OurTeam site={site} />
      <Marquee />
      <NigInfo site={site} />
      <Schedule items={data.schedules} site={site} />
      <EventsStrip items={data.events} site={site} />
      <Sermons items={data.sermons || []} site={site} />
      <DevotionBlock devotion={data.devotion} site={site} />
      <Komsel items={data.komsels} site={site} />
      <Gallery items={data.gallery} site={site} />
      <PastoralForm site={site} />
      <About site={site} />
      <Footer />
    </div>
  );
}
